import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const GOALS = [
  "Weight loss", "Weight gain", "Healthy lifestyle",
  "Pregnancy nutrition", "Diabetes-friendly diet", "Sports nutrition", "Other",
];
const ACTIVITY = ["Sedentary", "Lightly active", "Moderately active", "Very active", "Athlete"];
const DIETS = ["No restrictions", "Vegetarian", "Vegan", "Pescatarian", "Keto", "Halal", "Other"];

export default function HealthProfile() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    age: "", gender: "", weight_kg: "", height_cm: "",
    goal: "", activity_level: "", allergies: "", medical_conditions: "", diet_type: "", notes: "",
  });

  useEffect(() => {
    api.get("/health-profile").then(({ data }) => {
      if (data) setForm({ ...form, ...data, age: data.age?.toString() || "", weight_kg: data.weight_kg?.toString() || "", height_cm: data.height_cm?.toString() || "" });
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setVal = (k, v) => setForm({ ...form, [k]: v });

  const bmi = (() => {
    const w = parseFloat(form.weight_kg); const h = parseFloat(form.height_cm);
    if (!w || !h) return null;
    return (w / Math.pow(h / 100, 2)).toFixed(1);
  })();

  const next = () => setStep((s) => Math.min(3, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      const payload = {
        ...form,
        age: parseInt(form.age),
        weight_kg: parseFloat(form.weight_kg),
        height_cm: parseFloat(form.height_cm),
      };
      await api.post("/health-profile", payload);
      await refresh();
      toast.success("Health profile saved!");
      nav("/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const Field = ({ label, children }) => (
    <div>
      <label className="block text-sm font-medium text-ink mb-2">{label}</label>
      {children}
    </div>
  );
  const Pill = ({ active, onClick, children, testId }) => (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`rounded-full px-4 py-2 text-sm font-medium border transition-all ${active ? "bg-moss text-white border-moss" : "bg-white text-ink border-subtle hover:border-moss"}`}>
      {children}
    </button>
  );

  return (
    <div className="container-page py-12" data-testid="health-profile-page">
      <h1 className="font-heading text-3xl font-semibold text-ink">Your health profile</h1>
      <p className="mt-2 text-muted2 max-w-xl">A quick snapshot helps us tailor videos and PDF guides to your goals.</p>

      {/* Stepper */}
      <div className="mt-8 flex items-center gap-3" data-testid="profile-stepper">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-3">
            <span className={`w-8 h-8 rounded-full grid place-items-center text-sm font-semibold ${step >= s ? "bg-terra text-white" : "bg-cardm text-soft"}`}>{s}</span>
            {s < 3 && <span className={`w-12 h-0.5 ${step > s ? "bg-terra" : "bg-subtle"}`} />}
          </div>
        ))}
        <span className="ml-3 text-sm text-muted2">Step {step} of 3</span>
      </div>

      <div className="mt-8 bg-white border border-subtle rounded-2xl p-6 sm:p-8 shadow-ambient max-w-3xl">
        {step === 1 && (
          <div className="space-y-5" data-testid="step-1">
            <h2 className="font-heading text-xl font-semibold text-ink">Basic info</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Age">
                <input type="number" min="1" max="120" value={form.age} onChange={upd("age")}
                  data-testid="age-input"
                  className="w-full rounded-xl border border-subtle bg-white px-4 py-3 focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
              </Field>
              <Field label="Gender">
                <div className="flex gap-2 flex-wrap">
                  {["male", "female", "other"].map((g) => (
                    <Pill key={g} active={form.gender === g} onClick={() => setVal("gender", g)} testId={`gender-${g}`}>
                      {g[0].toUpperCase() + g.slice(1)}
                    </Pill>
                  ))}
                </div>
              </Field>
              <Field label="Weight (kg)">
                <input type="number" step="0.1" value={form.weight_kg} onChange={upd("weight_kg")}
                  data-testid="weight-input"
                  className="w-full rounded-xl border border-subtle bg-white px-4 py-3 focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
              </Field>
              <Field label="Height (cm)">
                <input type="number" step="0.1" value={form.height_cm} onChange={upd("height_cm")}
                  data-testid="height-input"
                  className="w-full rounded-xl border border-subtle bg-white px-4 py-3 focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
              </Field>
            </div>
            {bmi && (
              <div className="rounded-xl bg-cardm border border-subtle p-4 text-sm" data-testid="bmi-display">
                <span className="text-muted2">Auto-calculated BMI:</span> <strong className="text-ink ml-1">{bmi}</strong>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5" data-testid="step-2">
            <h2 className="font-heading text-xl font-semibold text-ink">Your goal & lifestyle</h2>
            <Field label="Main goal">
              <div className="flex gap-2 flex-wrap">
                {GOALS.map((g) => (
                  <Pill key={g} active={form.goal === g} onClick={() => setVal("goal", g)} testId={`goal-${g}`}>{g}</Pill>
                ))}
              </div>
            </Field>
            <Field label="Activity level">
              <div className="flex gap-2 flex-wrap">
                {ACTIVITY.map((a) => (
                  <Pill key={a} active={form.activity_level === a} onClick={() => setVal("activity_level", a)} testId={`activity-${a}`}>{a}</Pill>
                ))}
              </div>
            </Field>
            <Field label="Diet preference">
              <div className="flex gap-2 flex-wrap">
                {DIETS.map((d) => (
                  <Pill key={d} active={form.diet_type === d} onClick={() => setVal("diet_type", d)} testId={`diet-${d}`}>{d}</Pill>
                ))}
              </div>
            </Field>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5" data-testid="step-3">
            <h2 className="font-heading text-xl font-semibold text-ink">Health details</h2>
            <Field label="Food allergies (optional)">
              <input value={form.allergies} onChange={upd("allergies")} data-testid="allergies-input"
                placeholder="e.g., nuts, dairy" className="w-full rounded-xl border border-subtle bg-white px-4 py-3 focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
            </Field>
            <Field label="Medical conditions (optional)">
              <input value={form.medical_conditions} onChange={upd("medical_conditions")} data-testid="medical-input"
                placeholder="e.g., diabetes, hypertension" className="w-full rounded-xl border border-subtle bg-white px-4 py-3 focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
            </Field>
            <Field label="Notes (optional)">
              <textarea value={form.notes} onChange={upd("notes")} rows={4} data-testid="notes-input"
                placeholder="Anything else we should know?"
                className="w-full rounded-xl border border-subtle bg-white px-4 py-3 focus:border-moss focus:outline-none focus:ring-2 focus:ring-moss/20" />
            </Field>
          </div>
        )}

        {error && <p className="mt-4 text-sm text-danger">{error}</p>}

        <div className="mt-8 flex justify-between">
          <button type="button" onClick={prev} disabled={step === 1} data-testid="profile-back-button"
            className="rounded-full border-2 border-subtle text-ink px-6 py-2.5 font-semibold disabled:opacity-40 hover:border-moss hover:text-moss transition-all">Back</button>
          {step < 3 ? (
            <button type="button" onClick={next} data-testid="profile-next-button"
              className="rounded-full bg-moss hover:bg-moss-dark text-white px-6 py-2.5 font-semibold transition-all active:scale-95">Next</button>
          ) : (
            <button type="button" onClick={submit} disabled={busy} data-testid="profile-submit-button"
              className="inline-flex items-center gap-2 rounded-full bg-terra hover:bg-terra-dark text-white px-6 py-2.5 font-semibold transition-all active:scale-95 disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />} Save profile
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
